import type { CreatorPlatform } from '../shared/platforms';

// Canonical values used inside the creator onboarding feature.

/** Follower count on the creator's biggest platform, picked as a range. */
export const FOLLOWER_RANGES = [
	'UNDER_1K',
	'1K_10K',
	'10K_50K',
	'50K_100K',
	'100K_500K',
	'500K_PLUS',
] as const;
export type FollowerRange = (typeof FOLLOWER_RANGES)[number];

export const DEFAULT_PHONE_PREFIX = '+91';

export interface CreatorProfileDraft {
	readonly platform: CreatorPlatform;
	readonly profileUrl: string;
}

/** Editable values owned by the onboarding form. */
export interface CreatorDraft {
	readonly fullName: string;
	readonly email: string;
	readonly phonePrefix: string;
	readonly phoneNumber: string;
	readonly profiles: readonly CreatorProfileDraft[];
	readonly followerRange: FollowerRange | null;
	readonly couponCode: string;
}

/** A fresh value for the onboarding form; each call returns a new object. */
export function createEmptyCreatorDraft(): CreatorDraft {
	return {
		fullName: '',
		email: '',
		phonePrefix: DEFAULT_PHONE_PREFIX,
		phoneNumber: '',
		profiles: [],
		followerRange: null,
		couponCode: '',
	};
}

/**
 * Body for `POST creator-platform/api/v1/creators/applications`.
 * Personal details aren't sent; the backend reads them from the signed-in user.
 */
export interface CreatorApplicationSubmission {
	readonly coupon_code: string;
	readonly max_follower_count: number;
	readonly profiles: readonly {
		readonly platform: Uppercase<CreatorPlatform>;
		readonly profile_url: string;
	}[];
}

export const CREATOR_STATUSES = [
	'NOT_APPLIED',
	'PENDING',
	'APPROVED',
	'REJECTED',
] as const;
export type CreatorStatus = (typeof CREATOR_STATUSES)[number];

export const VERIFICATION_STATUSES = [
	'PENDING',
	'VERIFIED',
	'REJECTED',
] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

/** Products a live creator code works on. */
export const CREATOR_PRODUCTS = ['FLIGHT', 'HOTEL'] as const;
export type CreatorProduct = (typeof CREATOR_PRODUCTS)[number];

export interface CreatorProfile {
	readonly id: string;
	readonly platform: CreatorPlatform;
	readonly handle: string;
	readonly profileUrl: string;
	/** The channel or group name, when the creator gave one. */
	readonly displayName: string;
	/** Followers, subscribers or members, depending on the platform. */
	readonly followerCount: number | null;
	readonly verificationStatus: VerificationStatus;
	/** ISO timestamps; `verifiedAt` stays null until it's verified. */
	readonly addedAt: string | null;
	readonly verifiedAt: string | null;
}

/** The creator's application, normalized from the details API. */
export interface CreatorDetails {
	readonly status: CreatorStatus;
	/** True for new creators, and for rejected ones past the 30-day cooldown. */
	readonly canApply: boolean;
	readonly couponCode: string;
	/** Approved, but a WhatsApp/Telegram channel is still unverified. */
	readonly payoutHold: boolean;
	readonly rejectionReason: string;
	/** ISO-8601 UTC; when a rejected creator can apply again. */
	readonly reapplyAfter: string | null;
	readonly profiles: readonly CreatorProfile[];
	readonly products: readonly CreatorProduct[];
}

/** Where the live availability check for a creator code is at. */
export type CodeCheck =
	| 'idle'
	| 'checking'
	| 'available'
	| 'unavailable'
	| 'error';
